import React, { useEffect, useState, useCallback, useRef } from "react";
import { initializeLoopedSocket } from "./loopedSocketHelper";
import LoopedItinerary from "./LoopedItinerary";
import LoopedWaypointMap from "./LoopedWaypointMap";
import LoopedCurrentWaypoint from "./LoopedCurrentWaypoint";
import GeolocationHandler from "../LiveDispatch/GeolocationHandler";
import GpsWarningTag from "../LiveDispatch/GpsWarningTag";

import InitialStopSelectionModal from "./InitialStopSelectionModal";
import RouteCompletionStatus from "./RouteCompletionStatus";
import StopActionsModal from "./StopActionsModal";

import comms from "../../comms";
import useRouteGroupGPS from "../../../hooks/useRouteGroupGPS";

// Removed NoInspectionRequiredModal - completion handling now done by CompleteShift component

// Helper function to format date as HH:mm
const formatTimeHHMM = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return "00:00";
  }
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

function LoopedShuttlebus({
  socket: passedSocket,
  fleetSettings,
  userSettings,
  driverData,
  navigateTo,
  routeDetails,
  selectedVehicle,
  onSaveBreaks,
  onDeleteBreaks,
  onUpdateBreaks,
  onSaveEndPoint,
  onDeleteEndPoint,
  onRouteUpdate,
}) {
  const [jobDetails, setJobDetails] = useState([]);
  const [driverDetails, setDriverDetails] = useState(null);
  const [googleApiKey, setGoogleApiKey] = useState(null);
  const [map, setMap] = useState(null);
  const mapContainerRef = useRef(null);
  const [isAccurate, setIsAccurate] = useState(false);
  const [center, setCenter] = useState(null);
  const [currentEta, setCurrentEta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextStop, setNextStop] = useState(null);

  const [isPhone, setIsPhone] = useState(false);
  const [vehiclePosition, setVehiclePosition] = useState(null);
  
  // Looped schedule specific states
  const [loopedRouteData, setLoopedRouteData] = useState(null);
  const [currentLoopIteration, setCurrentLoopIteration] = useState(0);
  // Removed currentLoopStops - using calculatedItinerary as single source of truth
  const [showInitialStopModal, setShowInitialStopModal] = useState(false);

  const [showStopActionsModal, setShowStopActionsModal] = useState(false);
  const [selectedActionStop, setSelectedActionStop] = useState(null);

  const [nearestStopIndex, setNearestStopIndex] = useState(0);

  // New states for enhanced break/end point management
  const [startPointLog, setStartPointLog] = useState(null);
  const [breakLogs, setBreakLogs] = useState([]);
  const [endPointLog, setEndPointLog] = useState(null);
  const [canChangeStartPoint, setCanChangeStartPoint] = useState(true);
  const [canDeleteBreaks, setCanDeleteBreaks] = useState(true);
  const [canDeleteEndPoint, setCanDeleteEndPoint] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);

  // Dedicated state for end stop tracking by stop ID
  // activeEndStops persists across re-renders and is only cleared when driver departs from the specific stop
  // breakLogs is the single source of truth for break data
  const [activeEndStops, setActiveEndStops] = useState({}); // { stopId: { logId, completedAt, etc. } }

  // App's current location
  const [appLocation, setAppLocation] = useState(null);
  
  // Refs for GPS timing
  const lastLocationRef = useRef(null);

  // Add a state to force re-evaluation when route data changes
  const [routeDataVersion, setRouteDataVersion] = useState(0);
  
  // State for route completion
  const [isRouteComplete, setIsRouteComplete] = useState(false);
  
  // State for tracking beginning of shift
  const [isBeginShift, setIsBeginShift] = useState(false);

  // Add state to track if we should show the initial stop selection modal
  const [initialRouteData, setInitialRouteData] = useState(null);

  // Server-provided itinerary (server calculates all ETAs and sequencing)
  const [itinerary, setItinerary] = useState([]);

  // Add flag to track when socket updates are active
  const [hasSocketUpdate, setHasSocketUpdate] = useState(false);
  
  // Connection status state for toast display
  const [connectionError, setConnectionError] = useState(null);
  


  // Route group GPS — join room, send/receive peer positions, stale cleanup
  const { groupVehicles, sendGPS } = useRouteGroupGPS({
    selectedVehicle,
    routeDetails,
    fleetSettings,
  });

  // Removed old completion inspection states - now handled by CompleteShift component
//  const selectedVehicle = userSettings.dispatchData.vehicleObject || selectedVehicle;
  // Track whether we're on a phone vs tablet/desktop
  // Respect fleet setting 'displayOrientation' to force portrait or landscape layout
  useEffect(() => {
    const handleResize = () => {
      // Check if fleet setting 'displayOrientation' is set
      if (fleetSettings?.displayOrientation === 'portrait') {
        setIsPhone(true); // Force portrait layout
      } else if (fleetSettings?.displayOrientation === 'landscape') {
        setIsPhone(false); // Force landscape layout
      } else {
        // Default responsive behavior based on window width
        setIsPhone(window.innerWidth < 1200);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fleetSettings?.displayOrientation]);

  useEffect(() => {
    if (googleApiKey) {
      setLoading(false);
    }
  }, [googleApiKey]);

  // Subscribe to connection status changes for reconnection handling
  useEffect(() => {
    let wasDisconnected = false;
    
    const unsubscribe = comms.onConnectionStatusChange((status, previousStatus) => {
      // Track disconnection
      if (status === 'disconnected' || status === 'error') {
        wasDisconnected = true;
        setConnectionError(null); // Clear any previous error
      }
      
      // Show error toast on failed status
      if (status === 'failed') {
        setConnectionError('Connection failed. Tap the status icon to reconnect.');
      }
      
      // Clear error on successful connection
      if (status === 'connected') {
        setConnectionError(null);
        
        // Reload route data after reconnection
        if (wasDisconnected && routeDetails?.routeId) {
          comms.getRouteDetails(routeDetails.routeId)
            .then((updatedRouteData) => {
              if (updatedRouteData && typeof onRouteUpdate === 'function') {
                onRouteUpdate(updatedRouteData);
              }
            })
            .catch((err) => {
              console.error('Failed to reload looped route data after reconnection:', err);
            });
        }
        wasDisconnected = false;
      }
    });
    
    return unsubscribe;
  }, [routeDetails?.routeId, onRouteUpdate]);

  // Handle GPS errors from GeolocationHandler - fall back to first route stop
  const handleLocationError = useCallback((errorMsg) => {
    // If center is still null, try to fall back to first route stop
    if (!center && routeDetails?.itinerary?.length > 0) {
      const firstStop = routeDetails.itinerary.find(s => s.Latitude && s.Longitude);
      if (firstStop) {
        setCenter({ lat: parseFloat(firstStop.Latitude), lng: parseFloat(firstStop.Longitude) });
      }
    }
  }, [center, routeDetails]);

  const handleLocationUpdate = (newLocation) => {
    // Store the latest location
    lastLocationRef.current = newLocation;
    
    // Immediately update components for real-time functionality
    setCenter(newLocation);
    setVehiclePosition(newLocation); // Update vehicle position for map marker
    setAppLocation(newLocation); // Update app location for directions calculations
    if (map) {
      map.panTo(newLocation);
    }

    // Update nearest stop for looped route (immediate for auto arrive/depart)
    if (itinerary.length > 0) {
      updateNearestStop(newLocation);
    }
    
    // Send GPS update to server (throttled via hook)
    sendGPS(newLocation);
  };

  const handleSetBreaks = (newBreakLogs) => {
    setBreakLogs(newBreakLogs);
  };

  // Function to update the nearest stop based on current location
  const updateNearestStop = (location) => {
    if (!location || itinerary.length === 0) return;

    let minDistance = Infinity;
    let nearestIndex = 0;

    itinerary.forEach((stop, index) => {
      const stopLat = parseFloat(stop.Latitude);
      const stopLon = parseFloat(stop.Longitude);

      // Simple distance calculation (not haversine, just for comparison)
      const distance = Math.sqrt(
        Math.pow(location.lat - stopLat, 2) +
          Math.pow(location.lng - stopLon, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestIndex = index;
      }
    });

    setNearestStopIndex(nearestIndex);
  };

  // Function to check if route is completed
  const checkRouteCompletion = useCallback(() => {
    // Route is complete when:
    // 1. dispatchData.routeObject/"completeRouteData" object exists
    // 2. Has a populated endPointLog value
    // 3. Itinerary array is either empty OR the last log entry has the same StopId as the endPointLog's StopId
    // 4. OR when the last/only item in itinerary is in "arrived" state (new condition)
    
    if (!routeDetails) {
      return false;
    }

    // NEW: Check if the last/only item in itinerary is in arrived state
    if (itinerary && itinerary.length > 0) {
      // Get the current stop (should be the only one remaining or the last one)
      const currentStop = itinerary.find(stop => stop.currentWaypoint) || itinerary[itinerary.length - 1];
      
      // Check if this stop has arrived and is an endpoint
      if (currentStop && (currentStop.isArrived || currentStop.arrivedAt || currentStop.status === 'arrived')) {
        // Additional check: if this is the only stop left or it's marked as an endpoint
        if (itinerary.length === 1 || currentStop.endStep) {
          console.log('LoopedShuttlebus: Route completion detected - last/only stop has arrived:', currentStop);
          return true;
        }
      }
    }

    // Check if we have completeRouteData or the route object (for future extension)
    
    // Must have an endPointLog with a StopId
    if (!endPointLog || !endPointLog.StopId) {
      return false;
    }

    // Check itinerary conditions
    if (!itinerary || itinerary.length === 0) {
      // Empty itinerary means route is complete
      return true;
    }

    // Find the last stop entry that has been completed or logged
    const lastCompletedStop = itinerary
      .filter(stop => stop.completedTime || stop.departedAt || stop.status === 'completed')
      .pop();

    if (lastCompletedStop) {
      // If the last completed stop has the same StopId as the endPointLog, route is complete
      const lastStopId = lastCompletedStop.StopId || lastCompletedStop.FixedRoute_StopId;
      const endStopId = endPointLog.StopId || endPointLog.FixedRoute_StopId;
      
      return lastStopId === endStopId;
    }

    return false;
  }, [routeDetails, endPointLog, itinerary]);

  // Handler for socket-based itinerary updates using server-authoritative data
  const handleSocketRouteUpdate = useCallback((updateData) => {
    
    // Check if server provided complete route data (preferred approach)
    if (updateData.completeRouteData) {
      // Use server-provided complete route data directly
      setItinerary(updateData.completeRouteData.itinerary);
      
      // Update other route state from server data
      if (updateData.completeRouteData.currentWaypoint) {
        setNextStop(updateData.completeRouteData.currentWaypoint);
      }
      
      // Update break and endpoint logs from server
      if (updateData.updatedBreakLogs !== undefined) {
        setBreakLogs(updateData.updatedBreakLogs);
        if (onSaveBreaks) onSaveBreaks(updateData.updatedBreakLogs);
      }
      
      if (updateData.updatedEndPointLog !== undefined) {
        setEndPointLog(updateData.updatedEndPointLog);
        if (onSaveEndPoint) onSaveEndPoint(updateData.updatedEndPointLog);
      }
      
      // Notify parent component of complete route data update
      if (onRouteUpdate && typeof onRouteUpdate === 'function') {
        onRouteUpdate({
          ...updateData.completeRouteData,
          updateSource: 'socketUpdate',
          lastUpdated: new Date().toISOString()
        });
      }

      // Update isBeginShift based on completeRouteData.logs (matching FixedShuttlebus pattern)
      if (updateData.completeRouteData.logs !== undefined) {
        const logs = updateData.completeRouteData.logs;
        if (logs.length === 0) {
          setIsBeginShift(false);
        }
      }

      // Mark that we have socket updates to prevent routeDetails useEffect from overriding
      setHasSocketUpdate(true);
      // Increment route data version to prevent other useEffects from overriding
      setRouteDataVersion(prev => prev + 1);
      return;
    }
    
    // Handle updated break and endpoint logs from server (for all actions including ARRIVE/DEPART)
    if (updateData.updatedBreakLogs !== undefined) {
      setBreakLogs(updateData.updatedBreakLogs);
      if (onSaveBreaks) {
        onSaveBreaks(updateData.updatedBreakLogs);
      }
    }
    
    if (updateData.updatedEndPointLog !== undefined) {
      setEndPointLog(updateData.updatedEndPointLog);
      if (onSaveEndPoint) {
        onSaveEndPoint(updateData.updatedEndPointLog);
      }
    }
    
    // Check if this is an arrive action and set isBeginShift to false
    if (updateData.action === 'ARRIVE') {
      setIsBeginShift(false);
    }
    
    // Also check if we have logs data in the update to determine isBeginShift state
    if (updateData.logs !== undefined) {
      const logs = updateData.logs;
      if (logs.length === 0) {
        setIsBeginShift(true);
      } else if (logs.length === 1 && logs[0].PAXBoarded === -1) {
        setIsBeginShift(true);
      } else {
        setIsBeginShift(false);
      }
    }
  }, [onSaveBreaks, onSaveEndPoint, onRouteUpdate]);

  // Initialize isBeginShift based on initial route logs (only for fresh route loads, not socket updates)
  useEffect(() => {
    if (routeDetails && routeDetails.logs && !hasSocketUpdate) {
      const logs = routeDetails.logs;
      if (logs.length === 0) {
        setIsBeginShift(true);
      } else if (logs.length === 1 && logs[0].PAXBoarded === -1) {
        setIsBeginShift(true);
      } else {
        setIsBeginShift(false);
      }
    } else if (!routeDetails && !hasSocketUpdate) {
      setIsBeginShift(true); // Default to true if no route details
    }
  }, [routeDetails, hasSocketUpdate]);



  // Set initial nearest stop index when we have both current waypoint and job details
  useEffect(() => {
    if (
      loopedRouteData?.currentWaypoint &&
      jobDetails &&
      Array.isArray(jobDetails) &&
      jobDetails.length > 0
    ) {
      const currentStopId =
        loopedRouteData.currentWaypoint.Id ||
        loopedRouteData.currentWaypoint.stopId;
      const currentStopIndex = jobDetails.findIndex(
        (job) => job.Id === currentStopId || job.stopId === currentStopId
      );

      if (currentStopIndex >= 0 && currentStopIndex !== nearestStopIndex) {
        setNearestStopIndex(currentStopIndex);
      }
    }
  }, [loopedRouteData?.currentWaypoint, jobDetails, nearestStopIndex]);

  // Process server-provided itinerary (server now calculates ETAs)
  useEffect(() => {
    if (!routeDetails?.itinerary) {
      // Only clear if we don't have socket updates active
      if (!hasSocketUpdate) {
        setItinerary([]);
        setCurrentEta(null);
      }
      return;
    }

    // If we have active socket updates, don't override with routeDetails
    if (hasSocketUpdate) {
      return;
    }

    // Server now provides complete itinerary with calculated ETAs
    // Just enhance with any additional client-side state information
    const enhancedItinerary = routeDetails.itinerary.map(stop => {
      const stopId = stop.StopId || stop.ItineraryId || stop.Id;
      const hasActiveEndStop = activeEndStops[stopId];
      
      return {
        ...stop,
        // Standardize ID properties - use Id as the primary property
        Id: stopId,
        StopId: stopId, // Keep for backward compatibility
        // Add any additional client-side end stop information
        routeCompleted: !!hasActiveEndStop,
        completedAt: hasActiveEndStop?.completedAt || null,
        // Server provides ETAs, but add display formatting if needed
        displayTime: stop.completedTime ? 
          new Date(stop.completedTime).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }) :
          (stop.eta ? 
            new Date(stop.eta).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit',
              hour12: true 
            }) : null)
      };
    });

    setItinerary(enhancedItinerary);

    // Extract current waypoint ETA for legacy compatibility
    const currentWaypoint = enhancedItinerary.find(stop => stop.currentWaypoint);
    if (currentWaypoint && currentWaypoint.eta) {
      const etaDate = new Date(currentWaypoint.eta);
      setCurrentEta(formatTimeHHMM(etaDate));
    } else {
      setCurrentEta(null);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDetails?.itinerary, breakLogs, activeEndStops]);

  // Process route data from routeDetails prop - server is authoritative source
  useEffect(() => {
    if (!routeDetails) {
      return;
    }

    // If we have active socket updates, don't override with routeDetails
    // This prevents stale prop data from overriding fresh socket updates
    if (hasSocketUpdate) {
      return;
    }

    // Only reset socket update flag if this is explicitly a socket update
    // Don't reset it for regular prop updates that might be stale
    if (routeDetails.updateSource === 'socketUpdate') {
      setHasSocketUpdate(true);
    }

    // Check if initial stop selection is needed for looped routes
    if (routeDetails.itinerary) {
      const uniqueRunIds = [...new Set(routeDetails.itinerary.map(item => item.RunId))];
      const isLoopedSchedule = uniqueRunIds.length === 1;
      const isSocketUpdate = routeDetails.updateSource === 'socketUpdate';
      
      // Check if any progress has been made
      const hasActualProgress = routeDetails.itinerary.some(stop => 
        stop.completedTime || stop.beginStep || stop.arrivedAt || 
        stop.status === 'arrived' || stop.status === 'completed'
      ) || (routeDetails.logs.length > 0);
      
      // Show modal only for fresh looped routes with no progress and no start point selected
      const hasStartPointSelected = startPointLog !== null;
      if (isLoopedSchedule && !hasActualProgress && !isSocketUpdate && !hasStartPointSelected && routeDetails.itinerary.length > 0) {
        const stopsForModal = routeDetails.itinerary.map((stop, index) => ({
          Id: stop.StopId,
          ItineraryId: stop.ItineraryId,
          Name: stop.StopName || stop.LandmarkName,
          Lat: parseFloat(stop.Latitude),
          Lon: parseFloat(stop.Longitude),
          SortOrder: stop.SortOrder || index + 1,
          ETAOffsetMins: stop.ETAOffsetMins || 0,
        }));
        
        setInitialRouteData({ ...routeDetails, Stops: stopsForModal });
        setShowInitialStopModal(true);
        return;
      }
    }

    // Use server-authoritative data directly
    setBreakLogs(routeDetails.breakLogs || []);
    setEndPointLog(routeDetails.endPointLog || null);
    
    // Use server-provided itinerary directly (server is single source of truth)
    setItinerary(routeDetails.itinerary);
    
    // Create stops structure for legacy modal compatibility
    const tempStops = routeDetails.itinerary.map((stop, index) => ({
      Id: stop.Id || stop.StopId || stop.ItineraryId,
      Name: stop.StopName || stop.LandmarkName,
      SortOrder: stop.SortOrder || index + 1,
      ETAOffsetMins: stop.ETAOffsetMins || 0,
      RunId: stop.RunId,
      RunName: stop.RunName
    }));

    // Set up simplified looped route data structure
    const loopedRouteStructure = {
      ...routeDetails,
      Stops: tempStops,
      Runs: tempStops.length > 0 ? [{
        Id: tempStops[0].RunId || 'default-run',
        Name: tempStops[0].RunName || 'Default Run',
        Itinerary: tempStops.map(stop => ({
          StopId: stop.Id,
          SortOrder: stop.SortOrder,
          ETAOffsetMins: stop.ETAOffsetMins
        }))
      }] : [],
      breakLogs: routeDetails.breakLogs || [],
      endPointLog: routeDetails.endPointLog || null,
      canChangeStartPoint: routeDetails.canChangeStartPoint !== false,
      canDeleteBreaks: routeDetails.canDeleteBreaks !== false,
      canDeleteEndPoint: routeDetails.canDeleteEndPoint === true
    };
    
    setLoopedRouteData(loopedRouteStructure);



    // Initialize activeEndStops state from server-provided end log
    if (routeDetails.endPointLog) {
      const endLog = routeDetails.endPointLog;
      const initialActiveEndStops = {
        [endLog.FixedRoute_StopId || endLog.StopId]: {
          logId: endLog.Id,
          stopId: endLog.FixedRoute_StopId || endLog.StopId,
          stopName: endLog.StopName,
          completedAt: endLog.UpdatedOn,
          scheduledAt: endLog.CreatedOn
        }
      };
      setActiveEndStops(initialActiveEndStops);
    } else {
      setActiveEndStops({});
    }

    // Initialize management states
    if (routeDetails.startPointLog) {
      setStartPointLog(routeDetails.startPointLog);
    }
    
    setCanChangeStartPoint(routeDetails.canChangeStartPoint !== false);
    setCanDeleteBreaks(routeDetails.canDeleteBreaks !== false);
    setCanDeleteEndPoint(routeDetails.canDeleteEndPoint === true);
    
    // Check if currently on break using server-provided break logs
    const breakLogs = routeDetails.breakLogs || [];
    const activeBreakLog = breakLogs.find(log => !log.endTime);
    setIsOnBreak(!!activeBreakLog);
    
    // If center is still null (GPS hasn't fired yet), use first route stop as fallback
    if (!center && routeDetails.itinerary && routeDetails.itinerary.length > 0) {
      const firstStopWithCoords = routeDetails.itinerary.find(s => s.Latitude && s.Longitude);
      if (firstStopWithCoords) {
        setCenter({ lat: parseFloat(firstStopWithCoords.Latitude), lng: parseFloat(firstStopWithCoords.Longitude) });
      }
    }
    
    // Set nearest stop index based on current waypoint or next incomplete stop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDetails, fleetSettings, routeDataVersion, selectedVehicle]);

  // New useEffect to handle current waypoint and job details based on server-authoritative itinerary
  useEffect(() => {
    if (!itinerary || itinerary.length === 0) return;

    // Use server-determined current waypoint directly (server is authoritative)
    let currentStop = itinerary.find(stop => stop.currentWaypoint);
    
    // Server should always provide current waypoint, but fallback for safety
    if (!currentStop) {
      console.warn('Server did not provide currentWaypoint - using fallback logic');
      // Find first incomplete stop
      currentStop = itinerary.find(stop => !stop.completedTime && !stop.completed);
      
      if (!currentStop && itinerary.length > 0) {
        // All stops complete - use first stop for new loop iteration
        currentStop = itinerary[0];

        
        // Update itinerary to reset for new loop
        setItinerary(prevItinerary => {
          const resetItinerary = prevItinerary.map((stop, index) => ({
            ...stop,
            currentWaypoint: index === 0,
            completedTime: null,
            completed: false
          }));
          return resetItinerary;
        });
        
        setCurrentLoopIteration(prev => prev + 1);
      }
    }

    // Set current waypoint as nextStop
    if (currentStop) {
      // Convert calculatedItinerary stop to processedStops format for compatibility
      const nextStopFormatted = {
        Id: currentStop.StopId || currentStop.ItineraryId,
        Name: currentStop.StopName || currentStop.LandmarkName,
        Lat: parseFloat(currentStop.Latitude),
        Lon: parseFloat(currentStop.Longitude),
        SortOrder: currentStop.SortOrder,
        ETAOffsetMins: currentStop.ETAOffsetMins || 0,
        RunId: currentStop.RunId,
        RunName: currentStop.RunName,
        ScheduleId: currentStop.ScheduleId,
        completedTime: currentStop.completedTime,
        currentWaypoint: currentStop.currentWaypoint,
        eta: currentStop.eta,
        beginStep: currentStop.beginStep || false,
        endStep: currentStop.endStep || false,
        breakStep: currentStop.breakStep || false,
        breakTime: currentStop.breakTime || 0,
        breakDepartureTime: currentStop.breakDepartureTime,
        breakDuration: currentStop.breakDuration,
        completed: !!currentStop.completedTime,
        isBreak: currentStop.breakStep || false,
        minsToNext: currentStop.ETAOffsetMins || 5,
        Latitude: parseFloat(currentStop.Latitude),
        Longitude: parseFloat(currentStop.Longitude),
        StopIndex: currentStop.StopIndex,
        LandmarkId: currentStop.LandmarkId
      };

      setNextStop(nextStopFormatted);
      
      // Set nearest stop index
      const currentStopIndex = itinerary.findIndex(stop => stop.currentWaypoint);
      if (currentStopIndex !== -1) {
        setNearestStopIndex(currentStopIndex);
      }
    }

    // Set job details from itinerary (convert format for compatibility)
    const jobDetailsFromItinerary = itinerary.map(stop => ({
      Id: stop.StopId || stop.ItineraryId,
      Name: stop.StopName || stop.LandmarkName,
      Lat: parseFloat(stop.Latitude),
      Lon: parseFloat(stop.Longitude),
      SortOrder: stop.SortOrder,
      ETAOffsetMins: stop.ETAOffsetMins || 0,
      RunId: stop.RunId,
      RunName: stop.RunName,
      ScheduleId: stop.ScheduleId,
      completedTime: stop.completedTime,
      currentWaypoint: stop.currentWaypoint || false,
      eta: stop.eta,
      beginStep: stop.beginStep || false,
      endStep: stop.endStep || false,
      breakStep: stop.breakStep || false,
      breakTime: stop.breakTime || 0,
      breakDepartureTime: stop.breakDepartureTime,
      breakDuration: stop.breakDuration,
      completed: !!stop.completedTime,
      isBreak: stop.breakStep || false,
      minsToNext: stop.ETAOffsetMins || 5,
      Latitude: parseFloat(stop.Latitude),
      Longitude: parseFloat(stop.Longitude),
      StopIndex: stop.StopIndex,
      LandmarkId: stop.LandmarkId
    }));
    
    setJobDetails(jobDetailsFromItinerary);

  }, [itinerary]);



  useEffect(() => {
    const initialize = async () => {
      if (!fleetSettings) return;

      const driverAppConfig = fleetSettings;
      setGoogleApiKey(driverAppConfig.googleApiKey);

      // Always ensure we have a socket connection for communication
      if (!comms.getSocket()) {
        try {
          await comms.connectSocket(
            fleetSettings.fleetUrl || fleetSettings.dispatchUri
          );
        } catch (error) {
          console.error("Failed to establish socket connection:", error);
        }
      }
      let localSocket = passedSocket || comms.getSocket();
      const cleanup = await initializeLoopedSocket(
        localSocket,
        setJobDetails,
        setDriverDetails,
        userSettings,
        fleetSettings,
        handleSocketRouteUpdate // Use socket-based route update handler
      );
      return cleanup;
    };

    initialize();
  }, [
    passedSocket,
    fleetSettings,
    userSettings,
    routeDetails,
    handleSocketRouteUpdate,
  ]);

  // Monitor for route completion
  useEffect(() => {
    const isComplete = checkRouteCompletion();
    
    if (isComplete !== isRouteComplete) {
      setIsRouteComplete(isComplete);
      
      // Notify parent component of route completion status change
      if (onRouteUpdate && typeof onRouteUpdate === 'function' && routeDetails) {
        onRouteUpdate({
          ...routeDetails,
          isComplete: isComplete,
          completionStatusUpdated: new Date().toISOString(),
          updateSource: 'routeCompletion',
          lastUpdated: new Date().toISOString()
        });
      } else if (isComplete) {
        // Fallback: direct navigation if no onRouteUpdate callback
        console.log('LoopedShuttlebus: No onRouteUpdate callback, navigating directly to CompleteShift');
        navigateTo('CompleteShift');
      }
    }
  }, [checkRouteCompletion, isRouteComplete, routeDetails, endPointLog, itinerary, onRouteUpdate, navigateTo]);

  // Route completion is now handled by parent Dispatch component via onRouteUpdate
  // No direct navigation needed - parent will detect completion and route to CompleteShift

  const onLoad = useCallback((loadedMap) => {
    setMap(loadedMap);
  }, []);

  const displayTripInfo = (jobNumber) => {

  };

  const geolocationSuccess = (position) => {
    const { latitude, longitude } = position.coords;
    setCenter({ lat: latitude, lng: longitude });
    const newLocation = { lat: latitude, lng: longitude };
    setVehiclePosition(newLocation);
    setAppLocation({ lat: latitude, lng: longitude });
  };

  // Handle when user presses arrive button
  const handleArrive = useCallback(() => {
    setIsBeginShift(false);
  }, []);

  // Handle when endpoint is completed and navigate to CompleteShift
  const handleEndpointCompleted = useCallback(() => {
    if (navigateTo && typeof navigateTo === 'function') {
      navigateTo('CompleteShift');
    }
  }, [navigateTo]);

  const handleDirectionsCalculated = (duration) => {
    // Server now provides ETAs - directions duration is for informational purposes only
    // No longer used for ETA calculations
  };

  const handleRequestDirections = (requestCallback, showDirectionsCallback) => {
    if (itinerary.length > 0 && center && isAccurate) {
      // For looped routes, we handle directions in LoopedWaypointMap component
      showDirectionsCallback(true);
    } else {
      console.error(
        "Cannot request directions: Missing stops, center, or accuracy."
      );
    }
  };


  // Removed handleInspection - inspection is now handled by parent Dispatch component
  // Removed handleCompletionInspection - completion is now handled by CompleteShift component

  const handleMapMoved = (/* newCenter */) => {
    // Optionally handle map movement here
  };



  // Handle route begin from initial stop selection
  const handleRouteBegin = (startPointLog, updatedRoute) => {
    // Close the initial stop selection modal and clear initial route data
    setShowInitialStopModal(false);
    setInitialRouteData(null);
    setStartPointLog(startPointLog);
    // Trigger re-evaluation of route data by incrementing the version
    setRouteDataVersion((prev) => prev + 1);
    
    // Server will provide updated route data with calculated ETAs via socket update
  };

  const handleScheduleBreak = async (breakData) => {

    try {
      // Get route information from routeDetails

    
      const driverId = breakData.logData?.driverId;
      const stopId = breakData.logData?.stopId;
      const stopName = breakData.logData?.stopName;
      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
      const scheduleId = routeDetails?.scheduleId;
      if (!vehicleId || !routeId || !runId || !driverId || !scheduleId) {
        console.error("Missing required route information:", {
          vehicleId,
          routeId,
          runId,
          driverId,
          scheduleId,
        });
        throw new Error(
          "Missing required route information. Please try again after the route data loads."
        );
      }

      // Send break scheduling message via socket using comms module
      const breakMessage = {
        type: "LOOPED_ROUTE_BREAK",
        vehicleId: vehicleId,
        routeId: routeId,
        runId: runId,
        scheduleId: scheduleId,
        driverId: driverId,
        stopId: stopId,
        itineraryId: breakData.logData?.itineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
        stopName: stopName,
        breakDuration: breakData.logData?.breakDuration || breakData.breakDuration,
        departureTime: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        scheduledAt: new Date().toISOString(),
        location: breakData.currentLocation,
      };



      // Use comms module to send the message
      const messageString = `LOOPED_ROUTE_BREAK|${JSON.stringify(
        breakMessage
      )}`;
      comms.sendSocket(messageString);



      // Immediately add the break to local state for instant UI feedback
      const newBreakLog = {
        Id: `temp_${Date.now()}`, // Temporary ID until server confirms
        StopId: stopId,
        ItineraryId: breakData.logData?.itineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
        StopName: stopName,
        BreakDuration: breakData.logData?.breakDuration || breakData.breakDuration,
        DepartureTime: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        CreatedOn: new Date().toISOString(),
        UpdatedOn: breakData.logData?.departureTime || convertToISOString(breakData.departureTime),
        PAXBoarded: -2, // Break indicator
        VehicleId: vehicleId,
        DriverId: driverId,
        RunId: runId,
        ScheduleId: scheduleId
      };

      // Update local breakLogs immediately
      const updatedBreakLogs = [...breakLogs, newBreakLog];
      setBreakLogs(updatedBreakLogs);
      
      // breakLogs is now the single source of truth - no need to update activeBreaks
      
      // Call parent prop to update the parent component's state
      if (onSaveBreaks) {

        onSaveBreaks(updatedBreakLogs);
      }



    } catch (error) {
      console.error("Error scheduling break:", error);
      throw error; // Re-throw so modal can handle the error
    }
  };

  const convertToISOString = (timeStr) => {
    // Get today's date
    const today = new Date();
    const [time, modifier] = timeStr.split(" ");
    const [hours, minutes] = time.split(":");
    let hour = parseInt(hours, 10);

    if (modifier === "PM" && hour !== 12) {
      hour += 12;
    } else if (modifier === "AM" && hour === 12) {
      hour = 0;
    }

    today.setHours(hour, parseInt(minutes, 10), 0, 0);
    return today.toISOString();
  };

  // Handle deleting start point log
  const handleDeleteStartPoint = async () => {
    if (!startPointLog || !canChangeStartPoint) {
      return;
    }

    try {
      // Import the sendDeleteLogMessage function
      const { sendDeleteLogMessage } = await import("./loopedSocketHelper");

      sendDeleteLogMessage(startPointLog.Id, "START_POINT");

      // Refresh the schedule data
      setTimeout(() => {
        comms.sendSocket("START");
      }, 1000);
    } catch (error) {
      console.error("Error deleting start point:", error);
      alert("Failed to delete start point. Please try again.");
    }
  };

  // Handle deleting break log
  const handleDeleteBreak = async (deleteData) => {
    // Support both old format (breakLogId) and new format (comprehensive data object)
    const breakLogId = typeof deleteData === 'string' || typeof deleteData === 'number' 
      ? deleteData 
      : deleteData?.breakLogId;
      
    if (!breakLogId || !canDeleteBreaks) {
      return;
    }

    try {
      // Find the break log to get additional information
      const breakLog = breakLogs?.find(log => log.Id === breakLogId);
      if (!breakLog) {
        throw new Error('Break log not found');
      }
      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
      const scheduleId = routeDetails?.scheduleId;
      // Use comprehensive data object if provided, otherwise fallback to existing logic
      const deleteMessage = {
        logId: breakLogId,
        vehicleId: vehicleId,
        routeId: routeId,
        runId: runId,
        scheduleId: scheduleId,
        stopId: breakLog.StopId,
        itineraryId: breakLog.ItineraryId || routeDetails.itineraryId || (routeDetails.itinerary?.[0]?.ItineraryId),
        stopName: breakLog.StopName,
        driverId: deleteData?.driverId || userSettings?.Id,
        deletedAt: new Date().toISOString()
      };

      const messageString = `LOOPED_ROUTE_DELETE_BREAK|${JSON.stringify(deleteMessage)}`;
      comms.sendSocket(messageString);

    } catch (error) {
      console.error("Error deleting break:", error);
      alert("Failed to delete break. Please try again.");
    }
  };

  // Handle deleting end point log
  const handleDeleteEndPoint = async () => {
    if (!endPointLog || !canDeleteEndPoint) {
      return;
    }

    try {
      // Get route information from routeDetails
      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
      const scheduleId = routeDetails?.scheduleId;
      

      // Send delete end point message via socket using comms module
      const deleteMessage = {
        logId: endPointLog.Id,
        vehicleId: vehicleId,
        routeId: routeId,
        runId: runId,
        scheduleId: scheduleId,
        stopId: endPointLog.StopId,
        itineraryId: endPointLog.ItineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId),
        stopName: endPointLog.StopName,
        driverId: userSettings?.Id,
        deletedAt: new Date().toISOString()
      };

      const messageString = `LOOPED_ROUTE_DELETE_END|${JSON.stringify(deleteMessage)}`;
      comms.sendSocket(messageString);

      // Server will provide updated state via socket message - no local state manipulation needed

    } catch (error) {
      console.error("Error deleting end point:", error);
      alert("Failed to delete end point. Please try again.");
    }
  };

  const handleStopActionsSelect = (selectedStop) => {

    setSelectedActionStop(selectedStop);
    setShowStopActionsModal(true);
  };

  const handleCloseStopActionsModal = () => {
    setShowStopActionsModal(false);
    setSelectedActionStop(null);
  };

  const handleCompleteRoute = async (completionData) => {
    try {
      
      // Complete the route directly first (to ensure end log is saved)
      await completeRouteDirectly(completionData);
      
      // Route completion handling is now done by CompleteShift component
      // The automatic route completion detection will trigger navigation to completeShift
      
    } catch (error) {
      console.error("Error in route completion flow:", error);
      throw error;
    }
  };

  // Separate function to handle direct route completion
  const completeRouteDirectly = async (completionData) => {
    try {
      // Get route information from routeDetails
      
      const driverId = driverData.DriverId;
      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;
      const vehicleId = selectedVehicle?.id;
      
      if (!vehicleId || !routeId || !runId) {
        console.error("Missing required route information:", {
          vehicleId,
          routeId,
          runId,
        });
        throw new Error(
          "Missing required route information. Please try again after the route data loads."
        );
      }

      // Send route completion message via socket using comms module
      const completionMessage = {
        type: "LOOPED_ROUTE_END",
        vehicleId: completionData.logData?.vehicleId || vehicleId,
        routeId: completionData.logData?.routeId || routeId,
        runId: completionData.logData?.runId || runId,
        scheduleId: completionData.logData?.scheduleId || routeDetails?.scheduleId,
        driverId: completionData.logData?.driverId || driverId,
        stopId: completionData.logData?.stopId || completionData.stopId,
        itineraryId: completionData.logData?.itineraryId || routeDetails?.itineraryId || (routeDetails?.itinerary?.[0]?.ItineraryId) || null,
        stopName: completionData.logData?.stopName || completionData.stopName,
        completedAt: completionData.logData?.completedAt || new Date().toISOString(),
        location: completionData.currentLocation,
      };

      // Use comms module to send the message
      const messageString = `LOOPED_ROUTE_END|${JSON.stringify(
        completionMessage
      )}`;
      comms.sendSocket(messageString);

    } catch (error) {
      console.error("Error completing route:", error);
      throw error; // Re-throw so modal can handle the error
    }
  };

  const handleRestartRoute = async () => {


    try {
      // Get route information
      const vehicleId = userSettings?.vehicleId || selectedVehicle?.id;
      const driverId = userSettings?.Id;

      const routeId = routeDetails?.routeId;
      const runId = routeDetails?.runId;

      if (!vehicleId || !routeId || !runId) {
        console.error("Missing required route information:", {
          vehicleId,
          routeId,
          runId,
        });
        throw new Error("Missing required route information.");
      }

      // Send route restart message via socket using comms module
      const restartMessage = {
        type: "LOOPED_ROUTE_RESTART",
        vehicleId: vehicleId,
        driverId: driverId,
        routeId: routeId,
        runId: runId,
        scheduleId: routeDetails?.scheduleId,
        itineraryId: null, // Restart doesn't target a specific stop, but include for consistency
        restartedAt: new Date().toISOString(),
      };



      // Use comms module to send the message
      const messageString = `LOOPED_ROUTE_RESTART|${JSON.stringify(
        restartMessage
      )}`;
      comms.sendSocket(messageString);



      // Refresh the schedule data
      setTimeout(() => {
        comms.sendSocket("START");
      }, 1000);
    } catch (error) {
      console.error("Error restarting route:", error);
      alert("Failed to restart route. Please try again.");
    }
  };

  if (loading) {
    return <div>Loading looped schedule...</div>;
  }

  // Updated layout styles for portrait and landscape modes
  const containerClass = isPhone
    ? "flex flex-col h-full" // Portrait: vertical stack
    : "flex flex-row h-full"; // Landscape: horizontal split

  // Portrait mode: map takes 1/3, current stop 1/4, itinerary rest
  // Landscape mode: map and current stop split 50/50 on left, itinerary on right
  const mapSectionStyle = isPhone
    ? {
        height: "500px", // 1/3 of screen in portrait
        paddingLeft: "5px",
        paddingRight: "5px",
        marginTop:'10px',
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }
    : {
        height: "50%", // 50% of left side in landscape
        padding: "5px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      };

  const currentStopSectionStyle = isPhone
    ? {
        height: "auto", // 1/4 of screen in portrait
        paddingTop: "5px",
        paddingLeft: "5px",
        paddingRight: "5px",
        marginBottom: "5px",
      }
    : {
        height: "50%", // 50% of left side in landscape
        padding: "5px",
      };

  const itinerarySectionStyle = isPhone
    ? {
        height: "50%", 
        overflowY: "auto",
        paddingLeft: "5px",
        paddingRight: "5px",
        marginTop:'0px'
      }
    : {
        flex: "1", // Right side in landscape
        overflowY: "auto",
      };

  const leftSideStyle = isPhone
    ? {} // Not used in portrait
    : {
        width: "50%", // Left half in landscape
        display: "flex",
        flexDirection: "column",
      };

  // Inspection is now handled by the parent Dispatch component before routing here

  return (
    <div className="bg-slate-100" style={{ height: `var(--app-content-height)` }}>
      {/* Connection error toast */}
      {connectionError && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          {connectionError}
        </div>
      )}
      
      <GeolocationHandler
        onLocationUpdate={handleLocationUpdate}
        onLocationError={handleLocationError}
        setIsAccurate={setIsAccurate}
      />

      {googleApiKey && center && (
        <div className={containerClass}>
          {isPhone ? (
            // Portrait mode: vertical stack (map 1/3, current stop 1/4, itinerary remainder)
            <>
              {/* Map section - 1/3 of screen */}
                <div ref={mapContainerRef} style={mapSectionStyle}>
                  <LoopedWaypointMap
                    isPhone={isPhone}
                    center={center}
                    vehiclePosition={vehiclePosition}
                    appLocation={appLocation}
                    onMapLoad={onLoad}
                    waypoint={nextStop}
                    expectedTime={currentEta}
                    onDirectionsCalculated={handleDirectionsCalculated}
                    isAccurate={isAccurate}
                    handleRequestDirections={handleRequestDirections}
                    currentLoopStops={itinerary}
                    currentLoopIteration={currentLoopIteration}
                    onMapMoved={handleMapMoved}
                    groupVehicles={groupVehicles}
                    isBeginShift={isBeginShift}
                    routeDetails={routeDetails}
                    fleetSettings={fleetSettings}
                  />
                </div>              {/* Current Waypoint Section - 1/4 of screen */}
              {nextStop && (
                <div style={currentStopSectionStyle}>
                  <LoopedCurrentWaypoint
                    waypoint={nextStop}
                    expectedTime={currentEta}
                    isAccurate={isAccurate}
                    isPhone={isPhone}
                    currentLoopIteration={currentLoopIteration}
                    vehiclePosition={vehiclePosition}
                    currentLocation={center}
                    currentRun={loopedRouteData?.Runs?.[0] || null}
                    loopedRouteData={loopedRouteData}
                    userSettings={userSettings}
                    selectedVehicle={selectedVehicle}
                    routeDetails={routeDetails}
                    breakLogs={breakLogs}
                    endPointLog={endPointLog}
                    onArrive={handleArrive}
                    onEndpointCompleted={handleEndpointCompleted}
                  />
                </div>
              )}

              {/* Itinerary section - remaining space */}
              <div
                id="itinerary"
                style={itinerarySectionStyle}
              >
                <LoopedItinerary
                  nextStep={{ ...nextStop, nextETA: currentEta }}
                  jobs={itinerary}
                  onTripSelect={displayTripInfo}
                  onStopActionsSelect={handleStopActionsSelect}
                  breakLogs={breakLogs}
                  endPointLog={endPointLog}
                  activeEndStops={activeEndStops}
                />
              </div>
            </>
          ) : (
            // Landscape mode: left side (map + current stop), right side (itinerary)
            <>
              <div style={leftSideStyle}>
                {/* Map section - 50% of left side */}
                <div ref={mapContainerRef} style={mapSectionStyle}>
                  <LoopedWaypointMap
                    isPhone={isPhone}
                    center={center}
                    vehiclePosition={vehiclePosition}
                    appLocation={appLocation}
                    onMapLoad={onLoad}
                    waypoint={nextStop}
                    expectedTime={currentEta}
                    onDirectionsCalculated={handleDirectionsCalculated}
                    isAccurate={isAccurate}
                    handleRequestDirections={handleRequestDirections}
                    currentLoopStops={itinerary}
                    currentLoopIteration={currentLoopIteration}
                    onMapMoved={handleMapMoved}
                    groupVehicles={groupVehicles}
                    isBeginShift={isBeginShift}
                    routeDetails={routeDetails}
                    fleetSettings={fleetSettings}
                  />
                </div>

                {/* Current Waypoint Section - 50% of left side */}
                {nextStop && (
                  <div style={currentStopSectionStyle}>
                    <LoopedCurrentWaypoint
                      waypoint={nextStop}
                      expectedTime={currentEta}
                      isAccurate={isAccurate}
                      isPhone={isPhone}
                      currentLoopIteration={currentLoopIteration}
                      vehiclePosition={vehiclePosition}
                      currentLocation={center}
                      currentRun={loopedRouteData?.Runs?.[0] || null}
                      loopedRouteData={loopedRouteData}
                      userSettings={userSettings}
                      selectedVehicle={selectedVehicle}
                      routeDetails={routeDetails}
                      breakLogs={breakLogs}
                      endPointLog={endPointLog}
                      activeEndStops={activeEndStops}
                      onArrive={handleArrive}
                      onEndpointCompleted={handleEndpointCompleted}
                    />
                  </div>
                )}
              </div>

              {/* Itinerary section - right side */}
              <div
                id="itinerary"
                className="flex flex-col p-2"
                style={itinerarySectionStyle}
              >
                <LoopedItinerary
                  nextStep={{ ...nextStop, nextETA: currentEta }}
                  jobs={itinerary}
                  onTripSelect={displayTripInfo}
                  onStopActionsSelect={handleStopActionsSelect}
                  breakLogs={breakLogs}
                  endPointLog={endPointLog}
                  activeEndStops={activeEndStops}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Initial Stop Selection Modal */}
      {showInitialStopModal && initialRouteData && (
        <InitialStopSelectionModal
          isOpen={showInitialStopModal}
          onClose={() => {
            setShowInitialStopModal(false);
            setInitialRouteData(null);
          }}
          stops={initialRouteData.Stops || []}
          vehiclePosition={vehiclePosition || appLocation || center}
          googleApiKey={googleApiKey}
          routeId={initialRouteData.routeId || initialRouteData.Id}
          vehicleId={selectedVehicle?.id || selectedVehicle?.id}
          runId={initialRouteData.runId || (initialRouteData.itinerary && initialRouteData.itinerary[0]?.RunId)}
          onRouteBegin={handleRouteBegin}
          routeData={initialRouteData}
          userSettings={userSettings}
        />
      )}

      {!isAccurate && (
        <GpsWarningTag onLocationUpdate={handleLocationUpdate} setIsAccurate={setIsAccurate} />
      )}

      {/* Stop Actions Modal - combines break scheduling and route completion */}
      <StopActionsModal
        isOpen={showStopActionsModal}
        loopedRouteData={jobDetails}
        onClose={handleCloseStopActionsModal}
        selectedStop={selectedActionStop}
        currentLocation={appLocation}
        currentETA={currentEta}
        onScheduleBreak={handleScheduleBreak}
        onCompleteRoute={handleCompleteRoute}
        scheduledCompletionInfo={loopedRouteData?.scheduledCompletionInfo}
        startPointLog={startPointLog}
        breakLogs={breakLogs}
        onSetBreakLogs={handleSetBreaks}
        endPointLog={endPointLog}
        onSetEndPointLog={setEndPointLog}
        canChangeStartPoint={canChangeStartPoint}
        canDeleteBreaks={canDeleteBreaks}
        canDeleteEndPoint={canDeleteEndPoint}
        isOnBreak={isOnBreak}
        onDeleteStartPoint={handleDeleteStartPoint}
        onDeleteBreak={handleDeleteBreak}
        onDeleteEndPoint={handleDeleteEndPoint}
        userSettings={userSettings}
        routeDetails={routeDetails}
        driverData={driverDetails}
      />

      {/* Route Completion Status - show when route is completed */}
      {loopedRouteData && loopedRouteData.routeCompletionInfo && (
        <RouteCompletionStatus
          completionInfo={loopedRouteData.routeCompletionInfo}
          onRestart={handleRestartRoute}
        />
      )}
    </div>
  );
}

export default LoopedShuttlebus;
